export interface ColorOption {
  name: string;
  hex: string;
  image: string;
}

export type SizeGroup = "letter" | "number";

export type CategoryId =
  | "blusas"
  | "calcas"
  | "vestidos"
  | "macacoes"
  | "bermudas"
  | "conjuntos"
  | "saias";

export type ProductTag = "Novo" | "Destaque" | "Mais vendido";

export type TextTone = "light" | "dark";

export interface Product {
  id: string;
  code: string;
  name: string;
  categoryId: CategoryId;
  category: string;
  price: number;
  promoPrice?: number;
  installments: number;
  colors: ColorOption[];
  sizes: string[];
  sizesOut?: string[];
  sizeGroup: SizeGroup;
  tag?: ProductTag;
  isNew: boolean;
  sold: number;
  stock: number;
  description: string;
  fabric: string;
  fit: string;
  care: string;
}

export interface Category {
  id: CategoryId | "novidades";
  name: string;
  tagline: string;
  description: string;
  /** Banner principal (compatibilidade e uso genérico) */
  image: string;
  /** Banner horizontal para desktop (1920 × 760) */
  heroDesktop: string;
  /** Banner vertical para celular (900 × 1200) */
  heroMobile: string;
  /** Posição focal do banner no desktop (object-position) */
  heroFocalDesktop: string;
  /** Posição focal do banner no celular (object-position) */
  heroFocalMobile: string;
  /** Intensidade do escurecimento sobre o banner */
  heroOverlay: number;
  /** Tom do texto exibido sobre o banner */
  heroTextTone: TextTone;
}

export interface SizeRow {
  size: string;
  bust: string;
  waist: string;
  hip: string;
  length?: string;
}

export interface LookMoment {
  id: string;
  title: string;
  description: string;
  images: string[];
}

export interface InspirationItem {
  id: string;
  label: string;
  image: string;
  tall: boolean;
}

export interface NavItem {
  label: string;
  href: string;
}

export interface Benefit {
  id: string;
  icon: string;
  title: string;
  description: string;
}

export interface CartItem {
  key: string;
  productId: string;
  code: string;
  name: string;
  category: string;
  colorName: string;
  colorHex: string;
  size: string;
  quantity: number;
  unitPrice: number;
  image: string;
  stock: number;
}

/* ------------------------------------------------------------------ */
/* Compra e finalização                                                */
/* ------------------------------------------------------------------ */

export type DeliveryMethodId =
  | "retirada"
  | "entrega-local"
  | "pac"
  | "sedex"
  | "transportadora"
  | "onibus"
  | "consultar";

export interface DeliveryMethodConfig {
  id: DeliveryMethodId;
  name: string;
  description: string;
  deadline: string;
  /** null representa "Taxa a confirmar" */
  fee: number | null;
  regions: string;
  minOrder?: number;
  notice?: string;
  available: boolean;
  requiresCep?: boolean;
  requiresAddress?: boolean;
  requiresBus?: boolean;
}

export interface FreeShippingRule {
  id: string;
  label: string;
  enabled: boolean;
  methodIds: DeliveryMethodId[];
  minValue?: number;
  cities?: string[];
  districts?: string[];
  cepRanges?: { from: string; to: string }[];
  campaign?: { start: string; end: string } | null;
}

export interface CheckoutAddress {
  cep: string;
  street: string;
  number: string;
  complement: string;
  district: string;
  city: string;
  state: string;
  reference: string;
}

export interface CheckoutBus {
  destinationCity: string;
  destinationState: string;
  terminal: string;
  responsibleName: string;
  responsiblePhone: string;
}

export interface CheckoutForm {
  name: string;
  phone: string;
  email: string;
  city: string;
  state: string;
  consent: boolean;
  notes: string;
  deliveryMethod: DeliveryMethodId | "";
  address: CheckoutAddress;
  bus: CheckoutBus;
}

export interface ShippingResult {
  fee: number | null;
  feeLabel: string;
  freeApplied: boolean;
  ruleLabel?: string;
  message: string;
  applies: boolean;
}

/* ------------------------------------------------------------------ */
/* Camada de dados preparada para o futuro painel                      */
/* ------------------------------------------------------------------ */

export type CustomerStatus = "novo" | "recorrente" | "inativo";

export interface CustomerRecord {
  code: string;
  name: string;
  phone: string;
  phoneNormalized: string;
  email: string;
  address: CheckoutAddress;
  preferredDelivery: string;
  firstOrderAt: string;
  lastOrderAt: string;
  orderCount: number;
  totalSpent: number;
  origin: string;
  consent: boolean;
  notes: string;
  status: CustomerStatus;
}

export interface OrderItemRecord {
  productId: string;
  code: string;
  name: string;
  category: string;
  colorName: string;
  size: string;
  quantity: number;
  unitPrice: number;
  subtotal: number;
  image: string;
}

export interface OrderRecord {
  code: string;
  customerCode: string;
  createdAt: string;
  date: string;
  time: string;
  items: OrderItemRecord[];
  itemCount: number;
  subtotal: number;
  discount: number;
  shippingFee: number | null;
  total: number;
  deliveryMethodId: string;
  deliveryLabel: string;
  deliveryDeadline: string;
  freeShipping: boolean;
  address: CheckoutAddress | null;
  bus: CheckoutBus | null;
  notes: string;
  origin: string;
  status: string;
}

export const ORDER_STATUS = {
  started: "Pedido iniciado — aguardando atendimento",
  availability: "Disponibilidade confirmada",
  awaitingPayment: "Aguardando pagamento",
  paid: "Pago",
  packing: "Em separação",
  shipped: "Enviado",
  readyForPickup: "Pronto para retirada",
  delivered: "Entregue",
  cancelled: "Cancelado",
} as const;